import {
  Box,
  ButtonBase,
  Divider,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import Iconify from "../../components/iconify/Iconify";
import Scrollbar from "../../components/scrollbar/Scrollbar";
import { getMenuItems, hydrateMenu } from "../../reduxStore/slices/menuSlice";
import { useNotify } from "../../utils/snackbarUtils";

function CategoryList({ selectedCategory, searchTerm, onCategoryClick }) {
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const {
    items: categories,
    loading,
    error,
  } = useSelector((state) => state.menu);

  const { showSuccess, showError, handleApiError } = useNotify();

  const storeId = user.storeId;
  const STORAGE_KEY = `store_${storeId}_categories`;
  const EXPIRY_MINUTES = 720; // 12 hours

  const isEmpty = (obj) => !obj || Object.keys(obj).length === 0;

  useEffect(() => {
    if (!storeId) return;
    const localCategories = localStorage.getItem(STORAGE_KEY);
    if (localCategories) {
      try {
        const parsed = JSON.parse(localCategories);
        const now = Date.now();
        const isExpired =
          !parsed.timestamp ||
          now - parsed.timestamp > EXPIRY_MINUTES * 60 * 1000;

        if (!isExpired && parsed.data?.length > 0) {
          dispatch(hydrateMenu(parsed.data));

          if (isEmpty(selectedCategory)) {
            onCategoryClick(parsed.data[0]);
          }
          return;
        }
      } catch (e) {
        console.warn("Failed to parse cached categories", e);
      }
    }
    getAllMenuItems();
  }, []);

  useEffect(() => {
    if (categories?.length > 0) {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ data: categories, timestamp: Date.now() })
      );

      if (isEmpty(selectedCategory)) {
        onCategoryClick(categories[0]);
      }
    }
  }, [categories]);

  // This is for the search
  const filteredCategories = React.useMemo(() => {
    if (!searchTerm) return categories;
    const lower = searchTerm.toLowerCase();

    return categories
      .map((cat) => {
        const filteredItems =
          cat.products?.filter((item) =>
            item.product_name?.toLowerCase().includes(lower)
          ) || [];

        return {
          ...cat,
          product_count: filteredItems.length,
          products: filteredItems,
        };
      })
      .filter((cat) => cat.products.length > 0);
  }, [categories, searchTerm]);

  useEffect(() => {
    if (filteredCategories?.length > 0) {
      onCategoryClick(filteredCategories[0]);
    } else {
      onCategoryClick({});
    }
  }, [filteredCategories]);

  const handleRefresh = () => {
    getAllMenuItems(true);
  };

  const getAllMenuItems = async (force = false) => {
    if (!storeId) return;
    try {
      if (force) {
        localStorage.removeItem(STORAGE_KEY);
      }
      await dispatch(getMenuItems({ storeId })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  return (
    <>
      <Box className="menu-cat">
        <Box className="menu-cat-wrap">
          <Scrollbar
            sx={{
              height: 1,
              "& .simplebar-content": {
                height: 1,
                display: "flex",
                flexDirection: "column",
              },
            }}
          >
            <Box className="menu-cat-content">
              <Box
                sx={{
                  display: "flex",
                  gap: "2px",
                  padding: "8px 10px",
                  color: "#637381",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography variant="body2" noWrap>
                  Categories
                </Typography>
                <IconButton onClick={handleRefresh}>
                  <Tooltip title="Refresh">
                    <Iconify width="18px" icon="solar:refresh-bold-duotone" />
                  </Tooltip>
                </IconButton>
              </Box>
              <Divider />
              {filteredCategories?.map((category, index) => {
                const isActive = selectedCategory.name === category.name;
                return (
                  <Box
                    className={`menu-cat-card-wrap ${isActive ? "active" : ""}`}
                    key={category?.category_uid}
                  >
                    <ButtonBase
                      component="div"
                      style={{
                        width: "100%",
                      }}
                      onClick={() => onCategoryClick(category)}
                    >
                      <Box key={category.id} className="menu-cat-card">
                        <Box className="menu-cat-name">{category.name}</Box>
                        <Box className="menu-cat-items">
                          {category?.product_count} items
                        </Box>
                      </Box>
                    </ButtonBase>
                  </Box>
                );
              })}
            </Box>
          </Scrollbar>
        </Box>
      </Box>
    </>
  );
}

export default CategoryList;
